/**
 * VM-13c — Bank-Assistent
 *
 * Kachel-Wizard zur Bestätigung von Bankumsätzen. Buchung für Buchung:
 * "Bestätigen (weiter)" · "Zuordnung ändern" · "Ins Archiv verschieben".
 *
 * Zusätzlich:
 *  - Warnung wenn Empfänger-IBAN unbekannt (pending_iban_assignment=true)
 *  - Direkter "IBAN einem Eigentümer zuordnen"-Dialog
 *  - Plausibilitätswarnung, wenn Objekt-Eigentümer ≠ Konto-Inhaber
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { callWithMisdirect } from "@/components/bank/MisdirectPrompt";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatEUR, formatDate, POSTING_CATEGORIES } from "@/lib/constants";
import { BANK_MATCH_STATUS } from "@/lib/tickets";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  CheckCircle, Archive, ArrowRight, ArrowLeft, Warning, CaretLeft, Bank,
  ClockCounterClockwise, PencilSimple, Info, ArrowClockwise, Receipt,
} from "@phosphor-icons/react";

export default function BankAssistent() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [tenancies, setTenancies] = useState([]);
  const [stats, setStats] = useState({ unconfirmed: 0, archived: 0, pending_iban: 0 });
  const [pendingIbans, setPendingIbans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reassignFor, setReassignFor] = useState(null);
  const [ibanAssignFor, setIbanAssignFor] = useState(null);
  // iter159u — Filter für Eingänge/Ausgänge/Alle im Assistent
  const [directionFilter, setDirectionFilter] = useState("in"); // "in" | "out" | "all"

  const load = async () => {
    setLoading(true);
    try {
      const [txRes, statsRes, ibanRes, tenRes] = await Promise.all([
        api.get("/bank/transactions", { params: { only_unconfirmed: true, limit: 500 } }),
        api.get("/bank/assistant/stats"),
        api.get("/bank/pending-ibans"),
        api.get("/tenancies"),
      ]);
      setItems(txRes.data || []);
      setStats(statsRes.data);
      setPendingIbans(ibanRes.data || []);
      setTenancies(tenRes.data || []);
      setIdx(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // iter159u — Nur Buchungen der gewählten Richtung anzeigen
  const filteredItems = useMemo(() => {
    if (directionFilter === "all") return items;
    if (directionFilter === "in") return items.filter((it) => Number(it.amount || 0) > 0);
    return items.filter((it) => Number(it.amount || 0) < 0);
  }, [items, directionFilter]);
  useEffect(() => { setIdx(0); }, [directionFilter]);

  const current = filteredItems[idx];
  const total = filteredItems.length;

  const confirm = async () => {
    if (!current) return;
    try {
      await api.post(`/bank/transactions/${current.id}/confirm`);
      toast.success("Bestätigt");
      // aus Liste entfernen und Zeiger anpassen
      const next = items.filter((_, i) => i !== idx);
      setItems(next);
      setIdx((i) => Math.min(i, next.length - 1));
      setStats((s) => ({ ...s, unconfirmed: Math.max(0, s.unconfirmed - 1) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const archive = async () => {
    if (!current) return;
    // VM-14f — Kein Browser-Popup mehr. Direkt archivieren; Toast mit "Undo".
    const backup = current;
    try {
      const { data } = await api.delete(`/bank/transactions/${current.id}`);
      toast.success(
        `Archiviert${data.postings_deleted ? ` (${data.postings_deleted} Ist-Buchung${data.postings_deleted === 1 ? "" : "en"} entfernt)` : ""}`,
        {
          action: {
            label: "Rückgängig",
            onClick: async () => {
              try {
                await api.post(`/bank/transactions/${backup.id}/restore`);
                toast.success("Zurückgeholt");
                load();
              } catch { toast.error("Konnte nicht wiederhergestellt werden"); }
            },
          },
          duration: 6000,
        }
      );
      const next = items.filter((_, i) => i !== idx);
      setItems(next);
      setIdx((i) => Math.min(i, next.length - 1));
      setStats((s) => ({ ...s, unconfirmed: Math.max(0, s.unconfirmed - 1), archived: s.archived + 1 }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  // VM-14f — Vorschlag akzeptieren: direkt zuordnen und im UI markieren
  const acceptSuggestion = async (tenancyId) => {
    if (!current || !tenancyId) return;
    try {
      const data = await callWithMisdirect(async (extra) => (await api.patch(`/bank/transactions/${current.id}/reassign`, {
        tenancy_id: tenancyId, category: "rent_cold", ...extra,
      })).data);
      if (!data) return;
      toast.success(`Zugeordnet — ${data.posting_ids?.length || 0} Ist-Buchung${(data.posting_ids?.length === 1) ? "" : "en"} erzeugt`);
      const updated = [...items];
      const s = current.match_suggestions?.find(x => x.tenancy_id === tenancyId);
      updated[idx] = {
        ...current,
        matched_tenancy_id: tenancyId,
        contract_number: s?.contract_number,
        matched_tenant_name: s?.tenant_name,
        match_status: "manual",
        match_confidence: 0.9,
      };
      setItems(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const reassignSubmit = async (tenancyId) => {
    if (!current || !tenancyId) return;
    try {
      const data = await callWithMisdirect(async (extra) => (await api.patch(`/bank/transactions/${current.id}/reassign`, {
        tenancy_id: tenancyId, category: "rent_cold", ...extra,
      })).data);
      if (!data) return;
      toast.success(`Neu zugeordnet — ${data.posting_ids?.length || 0} Ist-Buchung(en) erzeugt.`);
      // Nach Reassign lokal aktualisieren
      const updated = [...items];
      const t = tenancies.find(x => x.id === tenancyId);
      updated[idx] = {
        ...current,
        matched_tenancy_id: tenancyId,
        contract_number: t?.contract_number,
        match_status: "manual",
        match_confidence: 0.9,
      };
      setItems(updated);
      setReassignFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler bei Neu-Zuordnung");
    }
  };

  const goto = (delta) => {
    setIdx((i) => Math.max(0, Math.min(filteredItems.length - 1, i + delta)));
  };

  // Tastatur: Pfeil links/rechts, Enter=Bestätigen
  useEffect(() => {
    const h = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight") goto(1);
      else if (e.key === "ArrowLeft") goto(-1);
      else if (e.key === "Enter" && !reassignFor && !ibanAssignFor) confirm();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line
  }, [items, idx, reassignFor, ibanAssignFor]);

  return (
    <div className="space-y-4" data-testid="bank-assistent-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav("/bank")} data-testid="bank-back-btn">
            <CaretLeft size={16} className="mr-1" /> Zurück
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Bank-Assistent</h1>
            <p className="text-sm text-slate-500 mt-1">
              Buchung für Buchung prüfen · Enter = bestätigen · ← / → = navigieren
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const { data } = await api.post("/bank/rematch-unmatched");
              toast.success(`${data.matched} neu zugeordnet, ${data.with_suggestions} mit Vorschlägen`);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
          }} className="gap-1" data-testid="rematch-btn">
            <ArrowClockwise size={12} /> Unzugeordnete neu prüfen
          </Button>
          {/* iter157 — Force-Rematch: auch bereits zugeordnete Buchungen erneut
              prüfen, damit neue Matching-Regeln (Verwendungszweck-Match,
              erweiterte Toleranz) auf ALLE unbestätigten Buchungen wirken. */}
          <Button variant="outline" size="sm" onClick={async () => {
            if (!await window._domivusConfirm("Alle unbestätigten Buchungen erneut durch das Auto-Matching laufen lassen? Bestehende (noch nicht bestätigte) Zuordnungen werden ersetzt, wenn eine bessere gefunden wird.")) return;
            try {
              const { data } = await api.post("/bank/rematch-unmatched?include_all=true");
              toast.success(`${data.rechecked} geprüft · ${data.matched} zugeordnet · ${data.posted} neue Buchungen · ${data.with_suggestions} mit Vorschlägen`, { duration: 8000 });
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
          }} className="gap-1 border-slate-400" data-testid="rematch-all-btn">
            <ArrowClockwise size={12} /> Alle neu prüfen
          </Button>
          <Link to="/archiv?tab=bank" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="bank-archive-link">
            <ClockCounterClockwise size={16} /> Archiv ({stats.archived})
          </Link>
        </div>
      </div>

      {/* iter159u — Filter: Eingänge / Ausgänge / Alle */}
      <div className="flex items-center gap-2 text-xs" data-testid="assistant-direction-filter">
        <span className="text-slate-500">Ansicht:</span>
        {[["in", "Nur Eingänge", "text-emerald-700"], ["out", "Nur Ausgänge", "text-rose-700"], ["all", "Alle", "text-slate-700"]].map(([k, label, cls]) => {
          const count = items.filter((it) => {
            if (k === "in") return Number(it.amount || 0) > 0;
            if (k === "out") return Number(it.amount || 0) < 0;
            return true;
          }).length;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setDirectionFilter(k)}
              className={`px-2.5 py-1 rounded-md border transition-colors ${directionFilter === k ? "bg-white border-slate-400 shadow-sm font-medium " + cls : "bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-700"}`}
              data-testid={`direction-filter-${k}`}
            >
              {label} <span className="ml-1 tabular-nums text-[10px] opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatBox label="Zu bestätigen" value={stats.unconfirmed} accent="text-amber-700" testid="stat-unconfirmed" />
        <StatBox label="Unbekannte IBAN" value={stats.pending_iban} accent="text-rose-700" testid="stat-pending-iban" />
        <StatBox label="Archiviert" value={stats.archived} accent="text-slate-500" testid="stat-archived" />
      </div>

      {/* Pending IBAN Banner */}
      {pendingIbans.length > 0 && (
        <div className="border-2 border-rose-200 bg-rose-50 rounded-md p-4" data-testid="pending-iban-banner">
          <div className="flex items-start gap-3">
            <Warning size={20} className="text-rose-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-sm text-rose-900">
                {pendingIbans.length} unbekannte Empfänger-IBAN{pendingIbans.length > 1 ? "s" : ""}
              </div>
              <div className="text-xs text-rose-700 mt-0.5 mb-2">
                Diese Konten sind noch keinem Eigentümer zugeordnet. Bitte zuweisen, damit Zahlungen automatisch dem richtigen Mietverhältnis zugeordnet werden.
              </div>
              <div className="space-y-1.5">
                {pendingIbans.map((p) => (
                  <div key={p.iban} className="flex items-center justify-between bg-white border border-rose-200 rounded px-3 py-2 text-sm" data-testid={`pending-iban-${p.iban}`}>
                    <div>
                      <span className="font-mono">{p.iban}</span>
                      <span className="text-xs text-slate-500 ml-2">
                        {p.count} Buchung{p.count > 1 ? "en" : ""} · {formatEUR(p.total_amount)}
                      </span>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setIbanAssignFor(p.iban)} data-testid={`assign-iban-btn-${p.iban}`}>
                      Eigentümer zuweisen
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wizard-Kachel */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-500">Lade…</div>
      ) : total === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center" data-testid="assistant-empty">
          <CheckCircle size={40} className="text-emerald-500 mx-auto mb-3" />
          <div className="text-lg font-medium text-slate-900">Alle Buchungen bestätigt</div>
          <div className="text-sm text-slate-500 mt-1">Es gibt derzeit keine offenen Bankbuchungen im Assistenten.</div>
          <Button variant="outline" className="mt-4" onClick={() => nav("/bank")} data-testid="assistant-goto-bank">
            Zur Bankbuchungs-Übersicht
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm" data-testid="assistant-card">
          <div className="border-b border-slate-100 px-4 py-2.5 flex items-center justify-between">
            <div className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
              Buchung {idx + 1} von {total}
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => goto(-1)} disabled={idx === 0} data-testid="assistant-prev-btn">
                <ArrowLeft size={14} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => goto(1)} disabled={idx === total - 1} data-testid="assistant-next-btn">
                <ArrowRight size={14} />
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {/* Zusammenfassung */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Datum</div>
                <div className="tabular-nums">{formatDate(current.booking_date)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Betrag</div>
                <div className={`text-lg font-semibold tabular-nums ${current.amount < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                  {formatEUR(current.amount)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Auftraggeber</div>
                <div>{current.counterparty_name || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">IBAN Auftraggeber</div>
                <div className="font-mono text-xs">{current.counterparty_iban || "—"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Verwendungszweck</div>
                <div className="text-slate-700">{current.purpose || current.booking_text || "—"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Empfängerkonto (Ihres)</div>
                <div className="font-mono text-xs">{current.bank_account_iban || "—"}</div>
              </div>
            </div>

            {/* Zuordnungs-Status */}
            <div className="border-t border-slate-100 pt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Zuordnung</div>
              {current.matched_tenancy_id ? (
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                  <div className="text-sm">
                    <span className="text-emerald-800 font-medium">
                      MV {current.contract_number || current.matched_tenancy_id.slice(0, 8)}
                    </span>
                    <span className="ml-2 text-xs text-emerald-700">
                      {(BANK_MATCH_STATUS[current.match_status] || {}).label || current.match_status}
                      {current.match_confidence ? ` · ${Math.round(current.match_confidence * 100)} %` : ""}
                    </span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setReassignFor(current.id)} data-testid="assistant-reassign-btn">
                    <PencilSimple size={12} className="mr-1" /> Ändern
                  </Button>
                </div>
              ) : current.matched_installment_plan_id ? (
                <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded px-3 py-2" data-testid="assistant-plan-badge">
                  <div className="text-sm text-indigo-800">
                    <Receipt size={14} className="inline mr-1" />
                    Abschlagsplan zugeordnet
                    <span className="ml-2 text-xs text-indigo-600 font-mono">{current.matched_installment_plan_id.slice(0, 8)}</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <div className="text-sm text-amber-800">
                    <Warning size={14} className="inline mr-1" />
                    Nicht zugeordnet
                    {current.plausibility_warning && (
                      <span className="block text-xs mt-0.5 text-rose-700">{current.plausibility_warning}</span>
                    )}
                  </div>
                  <Button size="sm" onClick={() => setReassignFor(current.id)} data-testid="assistant-assign-btn">
                    Zuordnen
                  </Button>
                </div>
              )}
            </div>

            {/* VM-14f — Zuordnungs-Vorschläge (bei nicht auto-gematchten Buchungen) */}
            {!current.matched_tenancy_id && (current.match_suggestions || []).length > 0 && (
              <div className="mt-2 space-y-1" data-testid="assistant-suggestions">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Vorschläge
                </div>
                {(current.match_suggestions || []).map((s) => (
                  <div key={s.tenancy_id} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded px-3 py-1.5" data-testid={`suggestion-${s.tenancy_id}`}>
                    <div className="text-sm flex-1 min-w-0">
                      <span className="font-mono text-xs text-blue-900">{s.contract_number || s.tenancy_id.slice(0, 8)}</span>
                      {s.tenant_name && <span className="ml-2 text-slate-700 truncate">{s.tenant_name}</span>}
                      <span className="ml-2 text-[11px] text-blue-700">
                        {s.reason === "iban" ? "IBAN-Treffer" : "Name+Betrag"} · {Math.round(s.confidence * 100)} %
                      </span>
                    </div>
                    <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700 text-xs gap-1" onClick={() => acceptSuggestion(s.tenancy_id)} data-testid={`accept-suggestion-${s.tenancy_id}`}>
                      Übernehmen
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {current.pending_iban_assignment && (
              <div className="border border-rose-200 bg-rose-50 rounded px-3 py-2 text-sm text-rose-800 flex items-start gap-2">
                <Warning size={14} className="mt-0.5" />
                <span>
                  Empfänger-IBAN <span className="font-mono">{current.bank_account_iban}</span> ist keinem Eigentümer zugeordnet. Zuweisung oben durchführen.
                </span>
              </div>
            )}
          </div>

          {/* Aktionen */}
          <div className="border-t border-slate-100 px-4 py-3 flex items-center justify-between bg-slate-50/40">
            <Button variant="outline" className="gap-1 text-rose-700 hover:text-rose-800" onClick={archive} data-testid="assistant-archive-btn">
              <Archive size={14} /> Ins Archiv
            </Button>
            <Button className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={confirm} data-testid="assistant-confirm-btn">
              <CheckCircle size={14} /> Bestätigen (Enter)
            </Button>
          </div>
        </div>
      )}

      {/* Reassign-Dialog */}
      <Dialog open={!!reassignFor} onOpenChange={(v) => !v && setReassignFor(null)}>
        <ReassignDialog
          tenancies={tenancies}
          txId={reassignFor}
          onSubmit={reassignSubmit}
          onClose={() => setReassignFor(null)}
        />
      </Dialog>

      {/* IBAN-Assign-Dialog */}
      <Dialog open={!!ibanAssignFor} onOpenChange={(v) => !v && setIbanAssignFor(null)}>
        <IbanAssignDialog
          iban={ibanAssignFor}
          onSuccess={() => { setIbanAssignFor(null); load(); }}
          onClose={() => setIbanAssignFor(null)}
        />
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, accent = "text-slate-900", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function ReassignDialog({ tenancies, txId, onSubmit, onClose }) {
  const [q, setQ] = useState("");
  // iter147 — Split-Preview Step
  const [pickedTenancy, setPickedTenancy] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const filtered = useMemo(() => {
    const norm = (s) => (s || "").toString().toLowerCase();
    const tenantStr = (t) => (Array.isArray(t.tenant_names) ? t.tenant_names.join(", ") : (t.tenant_name || ""));
    if (!q) return tenancies.slice(0, 30);
    const s = norm(q);
    return tenancies.filter((t) =>
      norm(t.contract_number).includes(s) ||
      norm(t.property_name).includes(s) ||
      norm(t.unit_label).includes(s) ||
      norm(tenantStr(t)).includes(s)
    ).slice(0, 50);
  }, [tenancies, q]);

  const pick = async (t) => {
    setPickedTenancy(t);
    setPreviewData(null);
    setPreviewErr(null);
    if (!txId) { onSubmit(t.id); return; }
    setPreviewBusy(true);
    try {
      const { data } = await api.get(`/bank/transactions/${txId}/split-preview`, { params: { tenancy_id: t.id } });
      setPreviewData(data);
    } catch (e) {
      setPreviewErr(e?.response?.data?.detail || "Split-Vorschau nicht möglich");
    } finally {
      setPreviewBusy(false);
    }
  };

  const fmtEur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const fmtMonth = (yyyyMm) => {
    if (!yyyyMm) return "—";
    const [y, m] = yyyyMm.split("-");
    const names = ["", "Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
    return `${names[parseInt(m, 10)] || m} ${y}`;
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>{pickedTenancy ? "Zuordnung bestätigen" : "Buchung neu zuordnen"}</DialogTitle>
      </DialogHeader>
      {!pickedTenancy && (
        <div className="space-y-3">
          <Input placeholder="Vertragsnummer, Mieter, Einheit oder Objekt…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reassign-search" />
          <div className="max-h-80 overflow-y-auto border border-slate-200 rounded divide-y" data-testid="reassign-list">
            {filtered.map((t) => {
              const tenants = Array.isArray(t.tenant_names) ? t.tenant_names.join(", ") : (t.tenant_name || "");
              return (
                <button
                  key={t.id}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex justify-between gap-3"
                  onClick={() => pick(t)}
                  data-testid={`reassign-option-${t.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-500 shrink-0">{t.contract_number}</span>
                      <span className="truncate">{tenants || "—"}</span>
                    </div>
                    {t.unit_label && (
                      <div className="text-[11px] text-slate-500 truncate">{t.unit_label}</div>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 shrink-0 truncate max-w-[40%]">{t.property_name}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="p-4 text-center text-sm text-slate-500">Keine Treffer</div>
            )}
          </div>
        </div>
      )}
      {pickedTenancy && (
        <div className="space-y-3" data-testid="reassign-preview">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Ziel-Mietverhältnis</div>
            <div className="font-medium">{pickedTenancy.contract_number}</div>
            <div className="text-xs text-slate-600">
              {Array.isArray(pickedTenancy.tenant_names) ? pickedTenancy.tenant_names.join(", ") : (pickedTenancy.tenant_name || "—")}
              {pickedTenancy.unit_label && ` · ${pickedTenancy.unit_label}`}
            </div>
          </div>
          {previewBusy && <div className="text-sm text-slate-500 text-center py-4">Berechne Split …</div>}
          {previewErr && (
            <div className="bg-rose-50 border border-rose-200 rounded p-2 text-sm text-rose-800" data-testid="reassign-preview-error">
              {previewErr}
            </div>
          )}
          {previewData && (
            <div className="bg-emerald-50/40 border border-emerald-200 rounded-md p-3">
              <div className="text-[11px] uppercase tracking-wider text-emerald-800 mb-2 font-semibold">
                Automatischer Split-Vorschlag · Zielmonat: {fmtMonth(previewData.target_month)}
              </div>
              <table className="w-full text-sm" data-testid="reassign-preview-table">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-emerald-200">
                    <th className="py-1">Kategorie</th><th>Zeitraum</th><th className="text-right">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {(previewData.splits || []).map((s, i) => (
                    <tr key={i} className="border-b border-emerald-100 last:border-b-0" data-testid={`reassign-split-${i}`}>
                      <td className="py-1.5 pr-2">
                        <span className="font-medium">{s.label}</span>
                        {s.reason && <div className="text-[11px] text-slate-500">{s.reason}</div>}
                      </td>
                      <td className="text-xs text-slate-600">{s.posting_date || "—"}</td>
                      <td className="text-right font-mono">{fmtEur(s.amount)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="py-2 pt-2.5" colSpan="2">Gesamt</td>
                    <td className="text-right font-mono py-2 pt-2.5">{fmtEur(previewData.total)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="text-[11px] text-emerald-800/80 mt-2">
                Die Aufteilung folgt dem BGB-Wasserfall (Kaltmiete zuerst, dann NK/HK/Stellplatz). Bei „Kaution“ im Verwendungszweck landet der volle Betrag direkt auf dem Kautionskonto.
              </div>
            </div>
          )}
        </div>
      )}
      <DialogFooter>
        {pickedTenancy ? (
          <>
            <Button variant="outline" onClick={() => { setPickedTenancy(null); setPreviewData(null); setPreviewErr(null); }} data-testid="reassign-back">
              Zurück
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={previewBusy || !!previewErr}
              onClick={() => onSubmit(pickedTenancy.id)}
              data-testid="reassign-confirm"
            >
              Bestätigen &amp; zuordnen
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
        )}
      </DialogFooter>
    </DialogContent>
  );
}

function IbanAssignDialog({ iban, onSuccess, onClose }) {
  const [owners, setOwners] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!iban) return;
    api.get("/contacts", { params: { role: "owner", limit: 200 } })
      .then((r) => setOwners(r.data || []))
      .catch(() => setOwners([]));
  }, [iban]);

  const submit = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await api.post("/bank/assign-iban", {
        iban, owner_contact_id: selected, rerun_matching: true,
      });
      toast.success(`IBAN zugewiesen. ${data.updated_transactions} Buchung(en) aktualisiert, ${data.rematched} neu gematcht.`);
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-md" data-testid="iban-assign-dialog">
      <DialogHeader>
        <DialogTitle>Empfänger-IBAN einem Eigentümer zuordnen</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">IBAN</div>
          <div className="font-mono text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">{iban}</div>
        </div>
        <div>
          <Label className="text-xs">Eigentümer</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="iban-owner-select"><SelectValue placeholder="Eigentümer auswählen…" /></SelectTrigger>
            <SelectContent>
              {owners.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {(o.first_name || o.last_name || o.company_name) ? `${o.first_name || ""} ${o.last_name || ""} ${o.company_name || ""}`.trim() : o.id}
                  {o.owner_number ? ` · Nr. ${o.owner_number}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-[11px] text-slate-500 flex gap-1 items-start">
          <Info size={12} className="mt-0.5" />
          <span>Die IBAN wird beim Eigentümer als Bankkonto hinterlegt. Alle betroffenen Buchungen werden neu geprüft und passenden Mietverhältnissen zugeordnet.</span>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={loading}>Abbrechen</Button>
        <Button onClick={submit} disabled={!selected || loading} data-testid="iban-assign-submit">
          {loading ? "Speichere…" : "Zuweisen"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
